import React from "react";
import { Card, CardFooter } from "@components/common/card";
import { Separator } from "@components/common/separator";
import { Badge } from "@components/common/badge";
import { cn } from "@pma/utils/helpers";
import { DictType } from "@pma/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@stores/auth-store";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import axios from "axios";
import {
  DEPARTMENTS_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { X } from "lucide-react";
import { Button } from "@components/common/button";
import { toast } from "@hooks/use-toast";

import PmsLoader from "@components/common/loader";
import useTranslator from "@hooks/use-translator";
import AddDepartment from "../users/add-department";

const DepartmentSettings = () => {
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { translate } = useTranslator();
  const { authConfig } = useAuth();

  const userId = authConfig?.id;
  const orgId = authConfig?.last_organization_id;

  const getDepartments = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${DEPARTMENTS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["departments", orgId],
    queryFn: getDepartments,
  });

  const deleteDepartment = async (id: number) => {
    const response = await axios.delete(
      SERVER_URL + DEPARTMENTS_DICTIONARY_ENDPOINT + `/delete/${id}`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response?.data;
  };

  const deleteDepartmentMutation = useMutation({
    mutationKey: ["delete-department"],
    mutationFn: (id: number) => deleteDepartment(id),
    onSuccess: () => {
      refetch();
      toast({
        title: translate("Success"),
        description: translate("Departments updated"),
        variant: "success",
      });
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data.message;
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to delete department"),
        variant: "error",
      });
    },
  });

  return (
    <Card className="p-3 min-h-[200px]">
      <div className="flex justify-between items-center">
        <h3 className="tex-lg font-semibold">{translate("Departments")}</h3>
      </div>

      <Separator className="my-2" />
      {isLoading && <PmsLoader />}
      <div className="flex gap-3 flex-wrap mt-4">
        {data?.map((department: DictType) => (
          <Badge className={cn("relative")} key={department?.id}>
            {translate(department?.name)}
            <Button
              variant={"outline"}
              size={"icon"}
              className="absolute -top-1 -right-2 h-3 w-3 hover:scale-125 text-red-500 rounded-full"
              onClick={() => deleteDepartmentMutation.mutate(department?.id)}
            >
              <X className="rounded-md" />
            </Button>
          </Badge>
        ))}
      </div>
      <CardFooter className="w-full py-4 justify-end">
        {/* Add new department */}
        <AddDepartment refetch={refetch} />
      </CardFooter>
    </Card>
  );
};

export default DepartmentSettings;
